<script setup lang="ts">
import { AuthSocialAuth } from '#components';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';

// --- Abas ---
const layoutStore = useLayoutStore();
const { authInitialTab } = storeToRefs(layoutStore);
const activeTab = ref<'login' | 'register'>(authInitialTab.value || 'login');

watch(activeTab, (newTab: 'login' | 'register') => {
  authInitialTab.value = newTab;
});

// --- Login logic ---
const { $metrics } = useNuxtApp();
const { t } = useTexts();
const { login, logout } = useAuthStore();
const { openToaster } = useLayoutStore();
const { visitorOrUserCountry } = useParameters();
const { currentRouteName, getSecureQueryParam } = useRoutes();
const {
  openForgotPassword,
  isCompleteRegulatoryRegisterEnabled,
  isRegulatorySimplifiedRegisterEnabled,
} = useAuth();

const { isAppSource } = useLayout();
const { userCanOnlyWithdraw } = useUserLimits();
const { isCaptchaLoginEnabled, isInvisibleCaptcha } = useCaptcha();

const route = useRoute();

const isLoadingLogin = ref(false);
const isReactivateAccountDialogVisible = ref(false);
const isForceChangePasswordVisible = ref(false);

const formValueLogin = ref('');
const formValuePassword = ref('');

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);
const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

const captchaComponentClass = computed(() => {
  if (!isCaptchaLoginEnabled.value)
    return 'absolute opacity-0 -z-10 pointer-events-none mt-0 mb-0';
  if (isInvisibleCaptcha.value) return 'mt-0 relative';

  return 'mt-2 -mb-4';
});

const loginFieldLabel = computed(() =>
  visitorOrUserCountry.value === 'BRA'
    ? t('auth.login.email_or_cpf')
    : t('auth.login.email_or_doc'),
);

const activeSubmitButton = computed(() => {
  if (formValueLogin.value.length < 5 || formValuePassword.value.length < 5)
    return false;
  if (isCaptchaLoginEnabled.value && !antiRobotToken.value) return false;

  return true;
});

const onCloseReactivateAccountDialog = () => {
  isReactivateAccountDialogVisible.value = false;
  antiRobotRef.value?.$resetAllCaptcha();
};

const onCloseForceChangePassword = () => {
  isForceChangePasswordVisible.value = false;
  openForgotPassword();
};

const submitFormLogin = (activate_account?: boolean) => {
  if (isCaptchaLoginEnabled.value && !antiRobotToken.value) {
    openToaster({
      message: t('auth.login.verify_captcha'),
      type: 'warning',
    });

    return;
  }
  if (!formValueLogin.value || formValueLogin.value.length < 5) {
    openToaster({ message: loginFieldLabel.value, type: 'error' });

    return;
  }
  if (!formValuePassword.value || formValuePassword.value.length < 6) {
    openToaster({
      message: t('auth.login.error.min_length_password'),
      type: 'error',
    });

    return;
  }

  const submitData: ILoginSubmitData = {
    login: formValueLogin.value,
    email: formValueLogin.value, // remover quando API atualizar
    password: formValuePassword.value,
    app_source: isAppSource.value ? 'app' : 'web',
    captcha_token: antiRobotToken.value,
    activate_account,
  };

  try {
    localStorage.setItem('temp_login_email', formValueLogin.value);
  } catch {}

  isLoadingLogin.value = true;
  setTimeout(() => submitApiLogin(submitData), 200);
};

const submitApiLogin = async (submitData: ILoginSubmitData) => {
  await logout({ displayMessage: false, redirectToHome: false });

  await login(submitData)
    .then(async (response) => {
      $metrics
        .push('gtm')
        ?.handle({ event: 'login', ...gtmUserParams(response.data.user) });

      const { isOnBetBrWithoutMigrateAccepted } = useUserValidations();

      if (isOnBetBrWithoutMigrateAccepted.value) {
        return await navigateTo(localeLink('/migrate/confirm'));
      }

      if (response.data.need_change_password) {
        const { setIsModalPasswordOpen } = useUserActivityStore();

        setTimeout(() => setIsModalPasswordOpen(true), 1000);
      }

      if (userCanOnlyWithdraw.value) {
        await navigateTo(localeLink('/user/wallet'));
      } else if (currentRouteName.value === 'login') {
        await navigateTo(localeLink('/'));
      }
    })
    .catch((err) => {
      const errorData = err?.data;

      isLoadingLogin.value = false;

      if (errorData?.type === 'cancelled_account') {
        isReactivateAccountDialogVisible.value = true;
      }
      if (errorData?.type === 'force_change_password') {
        isForceChangePasswordVisible.value = true;
      }
    })
    .finally(() => {
      antiRobotRef.value?.$resetAllCaptcha();
      if (currentRouteName.value !== 'login') isLoadingLogin.value = false;
    });
};

const tempLoginEmail = ref<string | null>(null);

onMounted(() => {
  const tabFromQuery = route.query.tab
    ? getSecureQueryParam(route.query.tab)
    : '';

  if (tabFromQuery === 'register' || authInitialTab.value === 'register') {
    activeTab.value = 'register';
  }

  const fromQuery = route.query.email
    ? getSecureQueryParam(route.query.email)
    : '';

  tempLoginEmail.value =
    fromQuery || localStorage.getItem('temp_login_email') || '';
  if (tempLoginEmail.value) formValueLogin.value = tempLoginEmail.value;
});
</script>

<template>
  <ClientOnly>
    <div :class="$style.pageContainer">
      <!-- force change password -->
      <AuthLoginForceChangePassword
        v-if="isForceChangePasswordVisible"
        @close="onCloseForceChangePassword"
      />

      <!-- Abas -->
      <div
        class="w-full self-stretch h-[50px] p-[7px] bg-[#0C1B44] rounded-[10px] flex items-center gap-[10px] text-white"
      >
        <button
          :aria-selected="activeTab === 'login'"
          :class="[
            'flex-1 self-stretch min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-[10px] inline-flex justify-center items-center gap-2 sm:gap-2.5 transition',
            activeTab === 'login'
              ? 'bg-[#008BFF]'
              : 'bg-transparent text-zinc-300 hover:text-white',
          ]"
          type="button"
          @click="activeTab = 'login'"
        >
          <span class="text-sm sm:text-base font-semibold leading-none truncate"
            >Entrar</span
          >
        </button>

        <button
          :aria-selected="activeTab === 'register'"
          :class="[
            'flex-1 self-stretch min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 rounded-[10px] inline-flex justify-center items-center gap-2 sm:gap-2.5 transition',
            activeTab === 'register'
              ? 'bg-[#008BFF]'
              : 'bg-transparent text-zinc-300 hover:text-white',
          ]"
          type="button"
          @click="activeTab = 'register'"
        >
          <span class="text-sm sm:text-base font-semibold leading-none truncate"
            >Criar nova conta</span
          >
        </button>
      </div>

      <!-- Conteúdo -->
      <form
        v-if="activeTab === 'login'"
        class="w-full flex flex-col gap-3 mt-5"
        @submit.prevent="submitFormLogin()"
      >
        <BaseLoading
          class="!bg-auth-bg-primary"
          :icon-loader="true"
          :show="isLoadingLogin"
        />

        <AuthLoginInput
          id="login"
          v-model="formValueLogin"
          autocomplete="off"
          :initial-value="tempLoginEmail || ''"
          :label="loginFieldLabel"
          name="login"
          placeholder="E-mail ou CPF"
          type="login"
        />

        <div class="password-field">
          <AuthLoginInput
            id="password"
            v-model="formValuePassword"
            autocomplete="current-password"
            :label="$t('auth.login.password')"
            name="password"
            placeholder="Senha"
            :show-hide-pass="true"
          />
        </div>

        <div class="self-stretch text-right text-zinc-400 text-sm sm:text-base">
          <a
            class="self-stretch text-right justify-center text-white/50 text-sm sm:text-base cursor-pointer transition-colors duration-150 hover:text-white decoration-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-sm"
            href="#"
            @click.prevent="openForgotPassword()"
          >
            {{ $t('auth.login.forgot_password') }}
          </a>
        </div>

        <div :class="$style.buttonBox">
          <AuthLoginSubmitButton
            :disabled="!activeSubmitButton"
            :label="$t('auth.login.login')"
            type="submit"
          />
        </div>

        <div :class="captchaComponentClass">
          <BaseFormAntiRobot ref="antiRobotRef" />
        </div>

        <!-- Social -->
        <AuthSocialAuth
          :is-login="true"
          :with-titles="true"
        />

        <!-- Reactivar conta -->
        <LazyBaseModalReactivateAccount
          v-model="isReactivateAccountDialogVisible"
          @close="onCloseReactivateAccountDialog"
          @reactivate="submitFormLogin(true)"
        />
      </form>

      <div
        v-else
        class="w-full self-stretch"
      >
        <AuthRegisterCompleteRegulatory
          v-if="
            isCompleteRegulatoryRegisterEnabled ||
            isRegulatorySimplifiedRegisterEnabled
          "
        />

        <AuthRegisterSingleStep v-else />
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.pageContainer {
  @apply w-full p-0 flex flex-col justify-start md:justify-center items-center overflow-x-hidden;
}

.buttonBox {
  @apply relative w-full;
  .btnSubmit {
    @apply rounded flex items-center justify-center gap-2 bg-auth-button-bg text-auth-button-text text-base font-semibold w-full px-3 py-3;
    background: radial-gradient(
      138% 168.3% at 36.76% -16.67%,
      #ff704a 0%,
      #ff3400 100%
    );
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 800;
    &[disabled]:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
    svg {
      @apply text-sm;
    }
  }
}

/* Ajusta o visual do input do Login e corrige autofill/focus do navegador */
:deep(.input-group) {
  background-color: #0c1b44 !important;
  border-radius: 10px !important;
  height: 3rem;
  display: flex;
  align-items: center;
}

:deep(.input-group .group) {
  background: transparent !important;
}

:deep(.input-group .input) {
  background-color: transparent !important;
  color: #cbd5e1 !important;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem;
  line-height: 1.5rem;
}

:deep(.input-group .input::placeholder) {
  color: #94a3b8 !important;
}

/* Força o mesmo fundo no autofill e no foco para evitar fundo cinza */
:deep(.input-group .input:-webkit-autofill),
:deep(.input-group .input:-webkit-autofill:hover),
:deep(.input-group .input:-webkit-autofill:focus) {
  -webkit-box-shadow: 0 0 0px 1000px #0c1b44 inset !important;
  -webkit-text-fill-color: #cbd5e1 !important;
  caret-color: #cbd5e1 !important;
  transition: background-color 9999s ease-out 0s !important;
}
</style>
